import { useEffect, useRef } from 'react'
import _L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

const L = _L as any

interface SalesZonesMapProps {
  sales: any[]
  height?: number
}

const LIMA_CENTER: [number, number] = [-12.05, -77.03]

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

// Low-to-high intensity gradient (green -> amber -> red), same semantic
// colors already used elsewhere in this app for low/medium/high. Color
// alone now carries "how many sales happened here" -- previously every
// zone was the same amber dot at varying opacity, which reads as a muddy
// blob once several overlap instead of a clear hot/cold signal.
const INTENSITY_STOPS: [number, string][] = [
  [0, '#22c55e'],
  [0.5, '#f59e0b'],
  [1, '#dc2626'],
]

function intensityColor(ratio: number): string {
  const clamped = Math.max(0, Math.min(1, ratio))
  let lo = INTENSITY_STOPS[0]
  let hi = INTENSITY_STOPS[INTENSITY_STOPS.length - 1]
  for (let i = 0; i < INTENSITY_STOPS.length - 1; i++) {
    if (clamped >= INTENSITY_STOPS[i][0] && clamped <= INTENSITY_STOPS[i + 1][0]) {
      lo = INTENSITY_STOPS[i]
      hi = INTENSITY_STOPS[i + 1]
      break
    }
  }
  const span = hi[0] - lo[0]
  const t = span === 0 ? 0 : (clamped - lo[0]) / span
  const [r1, g1, b1] = hexToRgb(lo[1])
  const [r2, g2, b2] = hexToRgb(hi[1])
  const r = Math.round(lerp(r1, r2, t))
  const g = Math.round(lerp(g1, g2, t))
  const b = Math.round(lerp(b1, b2, t))
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

function aggregateLocations(sales: any[]) {
  const zones = new Map<string, { lat: number; lng: number; count: number; total: number }>()

  sales.forEach((sale: any) => {
    const lat = parseFloat(sale.gps_latitude)
    const lng = parseFloat(sale.gps_longitude)
    if (isNaN(lat) || isNaN(lng)) return

    const key = `${lat.toFixed(4)},${lng.toFixed(4)}`
    const existing = zones.get(key)
    if (existing) {
      existing.count++
      existing.total += Number(sale.total_amount || 0)
    } else {
      zones.set(key, { lat, lng, count: 1, total: Number(sale.total_amount || 0) })
    }
  })

  return Array.from(zones.values())
}

// Calibrated against the max single-zone count across the FULL current
// `sales` dataset (not whatever's currently visible on screen), so a
// cluster's color stays stable as the user pans/zooms -- read from a ref
// by a stable `iconCreateFunction` closure instead of being recomputed
// per-cluster-group-instance.
function makeClusterIcon(cluster: any, maxZoneCount: number) {
  const count = cluster.getChildCount()
  const ratio = maxZoneCount > 0 ? count / maxZoneCount : 0
  const color = intensityColor(ratio)
  const size = Math.round(28 + Math.min(1, ratio) * 16) // 28-44px
  return L.divIcon({
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${color};border:2px solid #fff;
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-weight:600;font-size:${size > 36 ? 13 : 11}px;
      box-shadow:0 1px 4px rgba(0,0,0,0.35);
    ">${count}</div>`,
    className: '', // avoid leaflet.markercluster's default classed styling fighting with our inline styles
    iconSize: L.point(size, size),
  })
}

export default function SalesZonesMap({ sales, height = 400 }: SalesZonesMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const markersRef = useRef<any>(null)
  const maxZoneCountRef = useRef<number>(1)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current).setView(LIMA_CENTER, 12)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map)

    const markers = L.markerClusterGroup({
      maxClusterRadius: 60,
      iconCreateFunction: (cluster: any) => makeClusterIcon(cluster, maxZoneCountRef.current),
    }).addTo(map)
    mapRef.current = map
    markersRef.current = markers

    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      map.remove()
      mapRef.current = null
      markersRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!markersRef.current) return
    markersRef.current.clearLayers()

    const zones = aggregateLocations(sales)
    const maxCount = Math.max(...zones.map((z) => z.count), 1)
    maxZoneCountRef.current = maxCount

    zones.forEach((zone) => {
      const ratio = zone.count / maxCount
      const radius = 6 + ratio * 8
      const color = intensityColor(ratio)

      const circle = L.circleMarker([zone.lat, zone.lng], {
        radius,
        fillColor: color,
        color,
        weight: 2,
        opacity: 0.85,
        fillOpacity: 0.55 + ratio * 0.3,
      })

      circle.bindPopup(
        `<div style="text-align:center">
          <strong>${zone.count} venta${zone.count > 1 ? 's' : ''}</strong><br/>
          Ingresos: $${zone.total.toFixed(2)}
        </div>`
      )

      markersRef.current!.addLayer(circle)
    })
  }, [sales])

  return (
    <div style={{ position: 'relative', height: `${height}px` }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', borderRadius: '8px' }} />
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          zIndex: 1000,
          background: 'rgba(255,255,255,0.9)',
          borderRadius: '6px',
          padding: '6px 10px',
          fontSize: '11px',
          color: '#43291b',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      >
        <span>Menos ventas</span>
        <span
          style={{
            width: '60px',
            height: '10px',
            borderRadius: '5px',
            background: 'linear-gradient(to right, #22c55e, #f59e0b, #dc2626)',
          }}
        />
        <span>Más ventas</span>
      </div>
    </div>
  )
}
